import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

// Utility function for merging Tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format currency
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

// Format date
export function formatDate(date: string | Date | null | undefined): string {
  // Handle null, undefined, or invalid dates
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dateObj);
}

// Format date time
export function formatDateTime(date: string | Date | null | undefined): string {
  // Handle null, undefined, or invalid dates
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(dateObj);
}

// Format relative time
export function formatRelativeTime(date: string | Date | null | undefined): string {
  // Handle null, undefined, or invalid dates
  if (!date) return 'N/A';
  
  const target = new Date(date);
  // Check if date is valid
  if (isNaN(target.getTime())) {
    return 'Invalid Date';
  }
  
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  const now = new Date()
  const diffInSeconds = (target.getTime() - now.getTime()) / 1000
  
  if (Math.abs(diffInSeconds) < 60) {
    return rtf.format(Math.round(diffInSeconds), 'second')
  } else if (Math.abs(diffInSeconds) < 3600) {
    return rtf.format(Math.round(diffInSeconds / 60), 'minute')
  } else if (Math.abs(diffInSeconds) < 86400) {
    return rtf.format(Math.round(diffInSeconds / 3600), 'hour')
  } else {
    return rtf.format(Math.round(diffInSeconds / 86400), 'day')
  }
}

// Sleep utility for development/testing
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Generate random ID
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}
